import {
  HANDLING_UNIT_TYPES_THREEG,
  HAZMAT_CLASS_OPTIONS_THREEG,
  LTLAccessorial,
  LTLItem,
  PACKING_GROUP_OPTIONS_THREEG,
  QuickQuoteInputs,
  QuoteCountries,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { StopTypes } from 'types/Appointment';
import {
  LTLThreeGCommentPayload,
  LTLThreeGCompatibilityProfilePayload,
  LTLThreeGQuickQuoteLineItem,
  LTLThreeGQuickQuoteRequest,
  LTLThreeGQuoteChargePayload,
} from 'types/LTLQuickQuote';
import { Maybe, Undef } from 'types/UtilityTypes';

import { parseLocation } from '../../utils/location';
import {
  LTLCustomerData,
  LTLCustomerStaffData,
  normalizeNumericValue,
  roundToTwoDecimals,
} from './ltlRequestShared';

export type { LTLCustomerData, LTLCustomerStaffData };

// ThreeG form uses imperial only: dimensions in inches, weight in lbs. No conversion.
const IMPERIAL_DIMENSION_UNIT = 0;
const IMPERIAL_WEIGHT_UNIT = 0;

const CURRENCY = {
  code: '1',
  label: 'USD',
} as const;

const PACKING_GROUP_LABELS: Record<string, string> = Object.fromEntries(
  PACKING_GROUP_OPTIONS_THREEG.map((o) => [o.value, o.label])
);
const HAZMAT_CLASS_LABELS: Record<string, string> = Object.fromEntries(
  HAZMAT_CLASS_OPTIONS_THREEG.map((o) => [o.value, o.label])
);
const HANDLING_UNIT_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  HANDLING_UNIT_TYPES_THREEG.map((o) => [o.value, o.label])
);

type LTLFormValues = NonNullable<QuickQuoteInputs['ltl']>;
type QuoteCharge = NonNullable<LTLFormValues['quoteCharges']>[number];
type CommentItem = NonNullable<LTLFormValues['comments']>[number];
type CompatibilityProfileItem = NonNullable<
  LTLFormValues['compatibilityProfiles']
>[number];
type OptionalThreeGFields = Partial<LTLThreeGQuickQuoteRequest>;

type ChargeAmountField =
  | 'grossAmt'
  | 'netAmt'
  | 'discountAmt'
  | 'benchmark'
  | 'gainshare'
  | 'netCost'
  | 'thirdPartyCharge';
type ChargeCurrencyField =
  | 'grossAmtCurrency'
  | 'netAmtCurrency'
  | 'discountAmtCurrency'
  | 'benchmarkCurrency'
  | 'gainshareCurrency'
  | 'netCostCurrency'
  | 'thirdPartyChargeCurrency';
type ChargeCurrencyLabelField =
  | 'grossAmtCurrencyLabel'
  | 'netAmtCurrencyLabel'
  | 'discountAmtCurrencyLabel'
  | 'benchmarkCurrencyLabel'
  | 'gainshareCurrencyLabel'
  | 'netCostCurrencyLabel'
  | 'thirdPartyChargeCurrencyLabel';

function isNonEmptyString(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }

  return String(value).trim() !== '';
}

function hasNumericValue(value: unknown): boolean {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value);
  }

  if (typeof value === 'string') {
    if (value.trim() === '') {
      return false;
    }

    return Number.isFinite(Number(value));
  }

  return false;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function formatDateAsLocalYYYYMMDD(d: Date): string {
  const date = d instanceof Date ? d : new Date(d);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatChargeAmount(value: Undef<number>): Undef<string> {
  if (value == null || !Number.isFinite(value)) {
    return undefined;
  }

  return roundToTwoDecimals(value).toFixed(2);
}

function addChargeAmount(
  payload: LTLThreeGQuoteChargePayload,
  amount: Undef<number>,
  amountField: ChargeAmountField,
  currencyField: ChargeCurrencyField,
  currencyLabelField: ChargeCurrencyLabelField
): void {
  const formatted = formatChargeAmount(amount);
  if (formatted == null) {
    return;
  }

  payload[amountField] = formatted;
  payload[currencyField] = CURRENCY.code;
  payload[currencyLabelField] = CURRENCY.label;
}

function getHandlingUnitTypeName(
  handlingUnitTypeId: Undef<string>
): Undef<string> {
  if (!handlingUnitTypeId) {
    return undefined;
  }

  return HANDLING_UNIT_TYPE_LABELS[handlingUnitTypeId];
}

function buildThreeGLineItemHazmatFields(
  item: LTLItem
): Pick<
  LTLThreeGQuickQuoteLineItem,
  | 'linearLength'
  | 'minTemperature'
  | 'maxTemperature'
  | 'isNonStackable'
  | 'isHazmat'
  | 'hazmatContactName'
  | 'hazmatContactNumber'
  | 'properShippingName'
  | 'specialProvisions'
  | 'subsidiaryClass'
  | 'erapReference'
  | 'unClass'
  | 'packingGroup'
  | 'packingGroupLabel'
  | 'hazmatClassification'
  | 'hazmatClassificationLabel'
> {
  const isHazmat = Boolean(item.hazmat);

  const base: Pick<
    LTLThreeGQuickQuoteLineItem,
    | 'linearLength'
    | 'minTemperature'
    | 'maxTemperature'
    | 'isNonStackable'
    | 'isHazmat'
  > = {
    linearLength:
      item.linearLength != null
        ? roundToTwoDecimals(item.linearLength)
        : undefined,
    minTemperature:
      item.minTemp != null ? roundToTwoDecimals(item.minTemp) : undefined,
    maxTemperature:
      item.maxTemp != null ? roundToTwoDecimals(item.maxTemp) : undefined,
    isNonStackable: Boolean(item.nonStackable),
    isHazmat,
  };

  if (!isHazmat) {
    return base;
  }

  const result: Partial<LTLThreeGQuickQuoteLineItem> = { ...base };

  if (item.hazmatContactName) {
    result.hazmatContactName = item.hazmatContactName;
  }

  if (item.hazmatContactNumber) {
    result.hazmatContactNumber = item.hazmatContactNumber;
  }

  if (item.properShippingName) {
    result.properShippingName = item.properShippingName;
  }

  if (item.specialProvisions) {
    result.specialProvisions = item.specialProvisions;
  }

  if (item.subsidiaryClass) {
    result.subsidiaryClass = item.subsidiaryClass;
  }

  if (item.erapReference) {
    result.erapReference = item.erapReference;
  }

  if (item.unClass) {
    result.unClass = item.unClass;
  }

  const packingGroupId = item.packingGroup;
  if (packingGroupId) {
    result.packingGroup = packingGroupId;
    const packingGroupLabel = PACKING_GROUP_LABELS[packingGroupId];

    if (packingGroupLabel) {
      result.packingGroupLabel = packingGroupLabel;
    }
  }

  const hazmatClassId = item.hazmatClass;
  if (hazmatClassId) {
    result.hazmatClassification = hazmatClassId;
    const hazmatClassificationLabel = HAZMAT_CLASS_LABELS[hazmatClassId];

    if (hazmatClassificationLabel) {
      result.hazmatClassificationLabel = hazmatClassificationLabel;
    }
  }

  return result as Pick<
    LTLThreeGQuickQuoteLineItem,
    | 'linearLength'
    | 'minTemperature'
    | 'maxTemperature'
    | 'isNonStackable'
    | 'isHazmat'
    | 'hazmatContactName'
    | 'hazmatContactNumber'
    | 'properShippingName'
    | 'specialProvisions'
    | 'subsidiaryClass'
    | 'erapReference'
    | 'unClass'
    | 'packingGroup'
    | 'packingGroupLabel'
    | 'hazmatClassification'
    | 'hazmatClassificationLabel'
  >;
}

function buildThreeGLineItem(
  item: LTLItem,
  fallbackFreightClass: string
): LTLThreeGQuickQuoteLineItem {
  const description = item.description ?? '';
  const freightClass = item.freightClass ?? fallbackFreightClass;
  const handlingUnitTypeId = item.handlingUnitType;
  const handlingUnitTypeName = getHandlingUnitTypeName(handlingUnitTypeId);

  const base: LTLThreeGQuickQuoteLineItem = {
    commodityName: description,
    commodityExternalTmsId: item.commodityExternalTmsId ?? '',
    commodityDescription: description,
    totalWeight: item.weight ?? 0,
    quantityOfGoods: item.pieces ?? 1,
    handlingUnits: item.handlingUnits ?? 1,
    handlingUnitTypeID: handlingUnitTypeId,
    handlingUnitTypeName,
    freightClass,
    classManuallySelected: Boolean(item.freightClassLocked),
    length: item.length != null ? roundToTwoDecimals(item.length) : undefined,
    width: item.width != null ? roundToTwoDecimals(item.width) : undefined,
    height: item.height != null ? roundToTwoDecimals(item.height) : undefined,
  };

  return {
    ...base,
    ...buildThreeGLineItemHazmatFields(item),
  };
}

function buildThreeGLineItems(
  formValues: QuickQuoteInputs
): LTLThreeGQuickQuoteLineItem[] {
  const fallbackFreightClass = formValues.ltl?.commodity?.freightClass ?? '';
  const items = formValues.ltl?.items ?? [];

  return items.map((item) => buildThreeGLineItem(item, fallbackFreightClass));
}

function buildThreeGStops(
  formValues: QuickQuoteInputs
): LTLThreeGQuickQuoteRequest['stops'] {
  const { stops } = formValues;
  const lastStopIndex = stops.length - 1;

  return stops.map((stop, index) => {
    const parsedLocation = parseLocation(stop.location || '');
    const isFirstStop = index === 0;
    const isLastStop = index === lastStopIndex;

    const rawCountry =
      stop.country || parsedLocation?.country || QuoteCountries.USA;
    const countryValue = rawCountry === QuoteCountries.USA ? 'USA' : rawCountry;

    const address = {
      city: stop.city || parsedLocation?.city || '',
      state: stop.state || parsedLocation?.state || '',
      zip: stop.zip || parsedLocation?.zip || '',
      country: countryValue,
    };

    const baseStop = {
      order: index,
      stopType: isFirstStop ? StopTypes.Pickup : StopTypes.Dropoff,
      address,
    };

    if (isFirstStop && formValues.pickupDate) {
      return {
        ...baseStop,
        readyTime: formValues.pickupDate.toISOString(),
      };
    }

    if (isLastStop && formValues.deliveryDate) {
      return {
        ...baseStop,
        mustDeliver: formValues.deliveryDate.toISOString(),
      };
    }

    return baseStop;
  });
}

function buildCustomerData(
  ltlCustomer: Maybe<LTLCustomerData>,
  ltlCustomerStaff: Maybe<LTLCustomerStaffData>
): {
  name: string;
  tmsIntegrationId: number;
  ownerName: string;
  ownerId: number;
  externalTmsId: string;
} {
  return {
    name: ltlCustomer?.customerName ?? '',
    tmsIntegrationId: ltlCustomerStaff?.tmsIntegrationId ?? 0,
    ownerName: ltlCustomerStaff?.ownerName ?? '',
    ownerId: ltlCustomerStaff?.ownerId ?? 0,
    externalTmsId: ltlCustomer?.externalId ?? ltlCustomer?.customerId ?? '',
  };
}

function buildThreeGAccessorials(
  formValues: QuickQuoteInputs
): LTLThreeGQuickQuoteRequest['accessorials'] {
  const selectedAccessorials = formValues.ltl?.selectedAccessorials ?? [];

  return selectedAccessorials
    .filter((acc) => acc.externalId !== undefined && acc.externalId !== null)
    .map((acc) => ({ externalId: acc.externalId }));
}

function buildShipmentTotalsFields(
  shipmentTotals: LTLFormValues['shipmentTotals']
): OptionalThreeGFields {
  const fields: OptionalThreeGFields = {};

  if (shipmentTotals?.linesOverride !== undefined) {
    fields.linesOverride = shipmentTotals.linesOverride;
  }

  // Only send totals when linesOverride is true; omit them when false
  if (shipmentTotals?.linesOverride === true) {
    if (isFiniteNumber(shipmentTotals.wtGrsTot)) {
      fields.wtGrsTot = roundToTwoDecimals(shipmentTotals.wtGrsTot);
    }

    if (isFiniteNumber(shipmentTotals.wtNetTot)) {
      fields.wtNetTot = roundToTwoDecimals(shipmentTotals.wtNetTot);
    }

    if (isFiniteNumber(shipmentTotals.volGrsTot)) {
      fields.volGrsTot = roundToTwoDecimals(shipmentTotals.volGrsTot);
    }

    if (isFiniteNumber(shipmentTotals.volNetTot)) {
      fields.volNetTot = roundToTwoDecimals(shipmentTotals.volNetTot);
    }

    if (isFiniteNumber(shipmentTotals.pieceTot)) {
      fields.pieceTot = Math.round(shipmentTotals.pieceTot);
    }

    if (isFiniteNumber(shipmentTotals.handlingUnits)) {
      fields.handlingUnitsTot = Math.round(shipmentTotals.handlingUnits);
    }

    if (isFiniteNumber(shipmentTotals.linearLengthTot)) {
      fields.linearLengthTot = roundToTwoDecimals(
        shipmentTotals.linearLengthTot
      );
    }

    if (isFiniteNumber(shipmentTotals.billableWeight)) {
      fields.billableWeight = roundToTwoDecimals(shipmentTotals.billableWeight);
    }
  }

  return fields;
}

function buildQuoteExpirationFields(
  quoteExpirationDate: QuickQuoteInputs['quoteExpirationDate']
): OptionalThreeGFields {
  if (!quoteExpirationDate) {
    return {};
  }

  return {
    quoteExpirationDate: formatDateAsLocalYYYYMMDD(quoteExpirationDate),
  };
}

function buildRateInfoFields(
  rateInfo: LTLFormValues['rateInfo']
): OptionalThreeGFields {
  const fields: OptionalThreeGFields = {};

  if (rateInfo?.doNotOverwrite !== undefined) {
    fields.doNotOverwrite = rateInfo.doNotOverwrite;
  }

  if (rateInfo?.needsReview) {
    fields.reviewStatus =
      rateInfo.needsReview === 'Needs Review' ? 'NeedsReview' : 'Reviewed';
  }

  return fields;
}

function buildRatedServiceInfoFields(
  ratedServiceInfo: LTLFormValues['ratedServiceInfo']
): OptionalThreeGFields {
  const fields: OptionalThreeGFields = {};

  if (
    ratedServiceInfo?.ratedParcelService != null &&
    ratedServiceInfo.ratedParcelService !== ''
  ) {
    fields.ratedParcelService = ratedServiceInfo.ratedParcelService;
  }

  if (
    ratedServiceInfo?.ratedLtlService != null &&
    ratedServiceInfo.ratedLtlService !== ''
  ) {
    fields.ratedLtlService = ratedServiceInfo.ratedLtlService;
  }

  if (
    ratedServiceInfo?.ratedIntermodalService != null &&
    ratedServiceInfo.ratedIntermodalService !== ''
  ) {
    fields.ratedIntermodalService = ratedServiceInfo.ratedIntermodalService;
  }

  if (ratedServiceInfo?.estDelivery) {
    fields.estimatedDeliveryDate = formatDateAsLocalYYYYMMDD(
      ratedServiceInfo.estDelivery
    );
  }

  if (isFiniteNumber(ratedServiceInfo?.estTransitDays)) {
    fields.estimatedTransitDays = ratedServiceInfo.estTransitDays;
  }

  if (
    ratedServiceInfo?.directIndirect != null &&
    ratedServiceInfo.directIndirect !== ''
  ) {
    fields.directIndirect = ratedServiceInfo.directIndirect;
  }

  if (
    ratedServiceInfo?.quoteRefNum != null &&
    ratedServiceInfo.quoteRefNum !== ''
  ) {
    fields.quoteRefNumRated = ratedServiceInfo.quoteRefNum;
  }

  if (ratedServiceInfo?.effectiveDate) {
    fields.effectiveDateRated = formatDateAsLocalYYYYMMDD(
      ratedServiceInfo.effectiveDate
    );
  }

  if (ratedServiceInfo?.expirationDate) {
    fields.expirationDateRated = formatDateAsLocalYYYYMMDD(
      ratedServiceInfo.expirationDate
    );
  }

  return fields;
}

function buildQuoteCharges(
  quoteCharges: Undef<QuoteCharge[]>,
  accessorialDefinitions: LTLAccessorial[]
): OptionalThreeGFields {
  const meaningfulCharges = (quoteCharges ?? [])
    .map((charge, index): Maybe<LTLThreeGQuoteChargePayload> => {
      const hasValue =
        isNonEmptyString(charge.chargeType) ||
        isNonEmptyString(charge.accCode) ||
        hasNumericValue(charge.qty) ||
        (typeof charge.details === 'string' && charge.details.trim() !== '') ||
        isNonEmptyString(charge.rateType) ||
        isFiniteNumber(charge.grossAmt) ||
        isFiniteNumber(charge.netAmt) ||
        isFiniteNumber(charge.discAmt) ||
        isFiniteNumber(charge.benchmark) ||
        isFiniteNumber(charge.gainshare) ||
        isFiniteNumber(charge.netCost) ||
        isFiniteNumber(charge.thirdPartyChg);

      if (!hasValue) {
        return null;
      }

      const costTypeName =
        charge.chargeType === 'Freight' || charge.chargeType === 'Accessorial'
          ? charge.chargeType
          : 'Freight';

      const accessorial = charge.accCode
        ? accessorialDefinitions.find(
            (a) => String(a.externalId) === String(charge.accCode)
          )
        : undefined;

      const payload: LTLThreeGQuoteChargePayload = {
        costTypeName,
        accessorialCodeId: charge.accCode ?? undefined,
        accessorialCodeDesc: accessorial?.name ?? undefined,
        accessorialCodeLabel: accessorial?.code ?? undefined,
        accessorialQty: hasNumericValue(charge.qty)
          ? String(charge.qty)
          : undefined,
        chargeDetails: charge.details ?? undefined,
        dataValue_RateType: charge.rateType ?? undefined,
        uid: 0,
        boundindex: index,
        uniqueid: charge.chargeId ?? undefined,
        visibleindex: index,
      };

      addChargeAmount(
        payload,
        charge.grossAmt,
        'grossAmt',
        'grossAmtCurrency',
        'grossAmtCurrencyLabel'
      );
      addChargeAmount(
        payload,
        charge.netAmt,
        'netAmt',
        'netAmtCurrency',
        'netAmtCurrencyLabel'
      );
      addChargeAmount(
        payload,
        charge.discAmt,
        'discountAmt',
        'discountAmtCurrency',
        'discountAmtCurrencyLabel'
      );
      addChargeAmount(
        payload,
        charge.benchmark,
        'benchmark',
        'benchmarkCurrency',
        'benchmarkCurrencyLabel'
      );
      addChargeAmount(
        payload,
        charge.gainshare,
        'gainshare',
        'gainshareCurrency',
        'gainshareCurrencyLabel'
      );
      addChargeAmount(
        payload,
        charge.netCost,
        'netCost',
        'netCostCurrency',
        'netCostCurrencyLabel'
      );
      addChargeAmount(
        payload,
        charge.thirdPartyChg,
        'thirdPartyCharge',
        'thirdPartyChargeCurrency',
        'thirdPartyChargeCurrencyLabel'
      );

      return payload;
    })
    .filter(
      (payload): payload is LTLThreeGQuoteChargePayload => payload != null
    )
    .map((payload, index) => ({
      ...payload,
      uid: index,
      boundindex: index,
      visibleindex: index,
    }));

  if (meaningfulCharges.length === 0) {
    return {};
  }

  return { quoteCharges: meaningfulCharges };
}

function buildComments(
  commentItems: CommentItem[] | undefined
): OptionalThreeGFields {
  const meaningfulComments = (commentItems ?? [])
    .map((comment, index) => {
      const hasValue =
        (typeof comment.comment === 'string' &&
          comment.comment.trim() !== '') ||
        isNonEmptyString(comment.commentType) ||
        (typeof comment.stopNumeric === 'number' &&
          Number.isFinite(comment.stopNumeric));

      if (!hasValue) {
        return null;
      }

      return {
        value: comment.comment ?? '',
        qualifier: comment.commentTypeLabel ?? '',
        qualifierId: comment.commentType ?? '',
        stop: String(
          typeof comment.stopNumeric === 'number' &&
            Number.isFinite(comment.stopNumeric)
            ? comment.stopNumeric
            : ''
        ),
        orderLineNumber: 0,
        uid: 0,
        boundindex: 0,
        uniqueid:
          comment.id ??
          crypto.randomUUID?.() ??
          `comment-${index}-${Date.now()}`,
        visibleindex: 0,
      } as LTLThreeGCommentPayload;
    })
    .filter((payload): payload is LTLThreeGCommentPayload => payload != null)
    .map((payload, index) => ({
      ...payload,
      orderLineNumber: index + 1,
      uid: index,
      boundindex: index,
      visibleindex: index,
    }));

  if (meaningfulComments.length === 0) {
    return {};
  }

  return { comments: meaningfulComments };
}

function buildOtherConstraintsFields(
  otherConstraints: LTLFormValues['otherConstraints']
): OptionalThreeGFields {
  const fields: OptionalThreeGFields = {};

  fields.equipmentId = '1';
  if (
    otherConstraints?.equipment != null &&
    otherConstraints.equipment !== ''
  ) {
    fields.equipmentId = String(otherConstraints.equipment);
  }

  if (
    otherConstraints?.carrierTradingPartnerId != null &&
    otherConstraints.carrierTradingPartnerId !== ''
  ) {
    const carrierId = Number(otherConstraints.carrierTradingPartnerId);
    if (Number.isFinite(carrierId)) {
      fields.tradingPartnerIdCarrier = carrierId;
    }
  }

  if (
    otherConstraints?.parcelService != null &&
    otherConstraints.parcelService !== ''
  ) {
    fields.parcelService = otherConstraints.parcelService;
  }

  if (
    otherConstraints?.ltlService != null &&
    otherConstraints.ltlService !== ''
  ) {
    fields.ltlService = otherConstraints.ltlService;
  }

  if (
    otherConstraints?.intermodalService != null &&
    otherConstraints.intermodalService !== ''
  ) {
    fields.intermodalService = otherConstraints.intermodalService;
  }

  return fields;
}

function buildCompatibilityProfilesFields(
  compatibilityProfiles: Undef<CompatibilityProfileItem[]>
): OptionalThreeGFields {
  const meaningfulProfiles: LTLThreeGCompatibilityProfilePayload[] = [];

  for (const profile of compatibilityProfiles ?? []) {
    if (
      !profile.profileType ||
      !profile.profileName ||
      !profile.profileNameLabel
    ) {
      continue;
    }

    const profileId = Number(profile.profileName);
    if (!Number.isFinite(profileId) || profileId <= 0) {
      continue;
    }

    meaningfulProfiles.push({
      profileType: profile.profileType,
      profileName: profile.profileNameLabel,
      profileId,
    });
  }

  if (meaningfulProfiles.length === 0) {
    return {};
  }

  return { compatibilityProfile: meaningfulProfiles };
}

/**
 * Builds the LTL Quick Quote API request payload for ThreeG.
 * Line items use commodity-based shape: commodityName, commodityExternalTmsId, etc.
 * Values are sent as entered (imperial: in, lb) with no unit conversion.
 */
export type BuildLTLThreeGRequestOptions = {
  // When true, omit equipment/carrier/service/compatibility fields from the payload (UI hidden).
  hideConstraintSection?: boolean;
};

export function buildLTLThreeGRequest(
  formValues: QuickQuoteInputs,
  ltlCustomer: Maybe<LTLCustomerData>,
  ltlCustomerStaff: Maybe<LTLCustomerStaffData>,
  ltlQuoteRequestId: number,
  emailId: number,
  threadId: string,
  accessorialDefinitions: LTLAccessorial[] = [],
  options?: BuildLTLThreeGRequestOptions
): LTLThreeGQuickQuoteRequest {
  const lineItems = buildThreeGLineItems(formValues);
  const stops = buildThreeGStops(formValues);
  const customerData = buildCustomerData(ltlCustomer, ltlCustomerStaff);
  const threeGAccessorials = buildThreeGAccessorials(formValues);

  const shipmentValue = normalizeNumericValue(formValues.ltl?.value?.shipValue);

  const optionalThreeGFields: OptionalThreeGFields = {
    ...buildShipmentTotalsFields(formValues.ltl?.shipmentTotals),
    ...buildQuoteExpirationFields(formValues.quoteExpirationDate),
    ...buildRateInfoFields(formValues.ltl?.rateInfo),
    ...buildRatedServiceInfoFields(formValues.ltl?.ratedServiceInfo),
    ...buildQuoteCharges(formValues.ltl?.quoteCharges, accessorialDefinitions),
    ...buildComments(formValues.ltl?.comments),
    ...(options?.hideConstraintSection
      ? {}
      : {
          ...buildOtherConstraintsFields(formValues.ltl?.otherConstraints),
          ...buildCompatibilityProfilesFields(
            formValues.ltl?.compatibilityProfiles
          ),
        }),
  };

  return {
    emailId,
    threadId,
    quoteRequestId: ltlQuoteRequestId,
    transportType: formValues.transportType,
    customerExternalTmsId:
      ltlCustomer?.externalId ??
      ltlCustomer?.customerId ??
      formValues.customerName,
    customer: customerData,
    stops,
    lineItems,
    accessorials: threeGAccessorials,
    weightUnit: IMPERIAL_WEIGHT_UNIT,
    dimensionUnit: IMPERIAL_DIMENSION_UNIT,
    searchByTariff: false,
    stackable: formValues.ltl?.commodity?.stackable ?? false,
    isHazmat: formValues.ltl?.commodity?.hazmat ?? false,
    shipmentValue,
    isFrontOffice: true,
    shipmentType: 2,
    actualShipmentType: 2,
    trailerType: 0,
    pcmilerRoutingType: 0,
    linearFeet: formValues.ltl?.commodity?.linearFeet,
    ...optionalThreeGFields,
  };
}
